import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { HiUserGroup } from 'react-icons/hi';
import { authApi } from '../services/api';
import AuthTypeTabs from '../components/AuthTypeTabs';

const CommunityLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    try {
      await login(email, password, 'community_member');
      toast.success('Welcome back!');
      navigate('/community-account');
    } catch (error) {
      if (error.response?.status === 403) {
        setNeedsVerification(true);
      }
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await authApi.resendVerification(email);
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to resend verification email.');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-12 flex items-center" data-testid="community-login-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-md">
        <div className="bg-white rounded-2xl p-8 border border-neutral-200 animate-fade-in">
          <AuthTypeTabs active="community" mode="login" />

          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <HiUserGroup className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900">Community Member Login</h1>
            <p className="text-neutral-600">Welcome back to Explore MHC</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="form-label">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                data-testid="login-password-input"
              />
            </div>

            <button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          {needsVerification && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-center" data-testid="verify-email-notice">
              <p className="text-sm text-amber-800 mb-2">Your email hasn't been verified yet.</p>
              <button onClick={handleResend} className="text-sm text-[#005AA0] font-semibold hover:underline" data-testid="resend-verify-btn">
                Resend Verification Email
              </button>
            </div>
          )}

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
              Forgot Password?
            </Link>
          </div>

          <div className="mt-4 text-center space-y-2">
            <p className="text-neutral-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-semibold hover:underline">
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityLogin;
